// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0031-shared-wizard-settings-editors.md

import * as React from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InstallContactSchema, type InstallContact } from '@greenlight/shared';

const NAME_ERROR = 'Enter the name of the person Shift should contact.';
const EMAIL_ERROR = 'Enter a valid email address Shift can reach.';

export interface ContactEditorState {
  name: string;
  setName: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  /** Which field the current message belongs to, so the shell can mark it invalid. */
  errorField: 'name' | 'email' | null;
  error: string | null;
  isDirty: boolean;
  isValid: boolean;
  /** Returns the parsed contact, or null after surfacing the first problem. */
  validate: () => InstallContact | null;
  reset: () => void;
}

export function useContactEditor(initialName: string, initialEmail: string): ContactEditorState {
  const [name, setName] = React.useState(initialName);
  const [email, setEmail] = React.useState(initialEmail);
  const [validated, setValidated] = React.useState(false);

  React.useEffect(() => {
    setName(initialName);
    setEmail(initialEmail);
    setValidated(false);
  }, [initialName, initialEmail]);

  const parsed = InstallContactSchema.safeParse({ contact_name: name, contact_email: email });
  const errorField = parsed.success
    ? null
    : parsed.error.issues.some((issue) => issue.path[0] === 'contact_name')
      ? ('name' as const)
      : ('email' as const);
  const currentError =
    errorField === null ? null : errorField === 'name' ? NAME_ERROR : EMAIL_ERROR;

  return {
    name,
    setName,
    email,
    setEmail,
    errorField: validated ? errorField : null,
    error: validated ? currentError : null,
    isDirty: name.trim() !== initialName.trim() || email.trim() !== initialEmail.trim(),
    isValid: parsed.success,
    validate: () => {
      setValidated(true);
      return parsed.success ? parsed.data : null;
    },
    reset: () => {
      setName(initialName);
      setEmail(initialEmail);
      setValidated(false);
    },
  };
}

export interface ContactEditorProps {
  editor: ContactEditorState;
  disabled?: boolean;
  /** Distinguishes the two inputs' ids when both surfaces render on one page. */
  idPrefix?: string;
}

/**
 * The name and address Shift reaches this install at — collected once in the
 * install wizard and editable afterwards in Settings, from one editor so the
 * two surfaces cannot drift apart on validation or on what the fields mean
 * (ADR-0031). Each surface supplies its own shell, submit path, and error
 * presentation.
 */
export function ContactEditor({
  editor,
  disabled = false,
  idPrefix,
}: ContactEditorProps): React.JSX.Element {
  const generatedId = React.useId();
  const prefix = idPrefix ?? generatedId;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-name`}>Contact name</Label>
        <Input
          id={`${prefix}-name`}
          className="h-11 md:h-9"
          value={editor.name}
          onChange={(event) => editor.setName(event.target.value)}
          onBlur={() => editor.validate()}
          autoComplete="name"
          required
          disabled={disabled}
          aria-invalid={editor.errorField === 'name'}
          data-testid="contact-name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${prefix}-email`}>Contact email</Label>
        <Input
          id={`${prefix}-email`}
          className="h-11 md:h-9"
          // `type="email"` is for the mobile keyboard; the surfaces set
          // `noValidate` and report both fields through one styled message.
          type="email"
          value={editor.email}
          onChange={(event) => editor.setEmail(event.target.value)}
          onBlur={() => editor.validate()}
          autoComplete="email"
          required
          disabled={disabled}
          aria-invalid={editor.errorField === 'email'}
          data-testid="contact-email"
        />
        <p className="text-sm text-muted-foreground">
          A shared address works — this is a commercial contact, not a login.
        </p>
      </div>
    </div>
  );
}

/** What leaves the install and why — shown wherever the contact is editable. */
export function ContactDisclosure(): React.JSX.Element {
  return (
    <div
      className="rounded-[var(--radius)] border border-dashed px-4 py-3 text-sm text-muted-foreground"
      data-testid="contact-disclosure"
    >
      This name and address are sent to Shift, together with the identity of this install, so Shift
      can reach you about your subscription and billing. They are not used for anything else, and no
      other user's details ever leave this install.
    </div>
  );
}
